// Whiteboard engine — exports.
//   PNG: the board (or the selection) drawn at 1× to 8× (print), white or
//        see-through, with its DPI written into the file so a print or a
//        Word document sizes it right.
//   PDF: vector — one page the size of the board, or A4 … A1 / Letter,
//        portrait or landscape, with a footer naming the work item.
//   A marked-up drawing: one picture or PDF page and what is drawn on it,
//        cropped to its edges (a PDF: one page per drawing page, each on its
//        own sheet size).

import { boundsOf, byZ, center } from './scene.js';
import { expandBox, rotate, boxOfPoints, corners } from './geometry.js';
import { drawCanvas, drawPdf } from './render.js';

export const PAGE_SIZES = {
  A4: [595.28, 841.89], A3: [841.89, 1190.55], A2: [1190.55, 1683.78], A1: [1683.78, 2383.94], A0: [2383.94, 3370.39], Letter: [612, 792],
};
// The largest canvas every desktop browser draws (side and area).
export const MAX_SIDE = 16384;
export const MAX_PIXELS = 120e6;

export function exportBox(elements, padding = 32) {
  const b = boundsOf(elements);
  return b ? expandBox(b, padding) : null;
}
export function pngSize(box, scale) {
  return { w: Math.max(1, Math.ceil(box.w * scale)), h: Math.max(1, Math.ceil(box.h * scale)) };
}
// The highest scale this board can be drawn at.
export function maxScale(box) {
  if (!box) return 1;
  return Math.max(0.1, Math.min(MAX_SIDE / box.w, MAX_SIDE / box.h, Math.sqrt(MAX_PIXELS / (box.w * box.h))));
}

// ---- a marked-up drawing --------------------------------------------------------------
// The drawings on the board — its pictures and PDF pages: each PDF's pages in
// order, then the pictures top to bottom.
export function drawingsOf(elements) {
  const pad = (n) => String(Math.round(n + 1e6)).padStart(9, '0');
  const key = (e) => (e.doc ? `0 ${e.doc.name} ${e.doc.fileId} ${pad(e.doc.page)}` : `1 ${pad(e.y)} ${pad(e.x)}`);
  return elements.filter((e) => e.type === 'image' && !e.deleted).sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0));
}
// How a drawing is named in the export dialog and footer (a page brought in
// twice: "copy 2").
export function drawingLabel(el, drawings = []) {
  if (el?.doc) {
    const same = drawings.filter((d) => d.doc && d.doc.fileId === el.doc.fileId && d.doc.page === el.doc.page);
    const copy = same.length > 1 ? ` (copy ${same.findIndex((d) => d.id === el.id) + 1})` : '';
    return `${el.doc.pages > 1 ? `${el.doc.name} · page ${el.doc.page} of ${el.doc.pages}` : el.doc.name}${copy}`;
  }
  const pics = drawings.filter((d) => !d.doc);
  return pics.length > 1 ? `Picture ${pics.findIndex((d) => d.id === el?.id) + 1}` : 'Picture';
}
// The drawing's edges (what the export crops to).
export function drawingFrame(el) {
  return boundsOf([el]);
}
// The drawing and everything that reaches onto it; the frame crops the rest.
export function markedUp(elements, drawing) {
  const f = drawingFrame(drawing);
  if (!f) return [];
  return elements.filter((e) => {
    if (e.deleted) return false;
    if (e.id === drawing.id) return true;
    const b = boundsOf([e]);
    return !!b && b.x < f.x + f.w && b.x + b.w > f.x && b.y < f.y + f.h && b.y + b.h > f.y;
  });
}
// The paper a PDF page was drawn on (it is placed at its real size: 1 pt = 96/72 board units).
export function paperOf(w, h) {
  const lo = (Math.min(w, h) * 72) / 96; const hi = (Math.max(w, h) * 72) / 96;
  for (const [name, [a, b]] of Object.entries(PAGE_SIZES)) {
    if (Math.abs(lo - a) / a < 0.03 && Math.abs(hi - b) / b < 0.03) return name;
  }
  return null;
}

// ---- PNG -----------------------------------------------------------------------------
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
  return t;
})();
function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}
// Write a pHYs chunk (pixels per metre) after IHDR.
export function withDpi(png, dpi) {
  const ppm = Math.round(dpi / 0.0254);
  const chunk = new Uint8Array(21);
  const dv = new DataView(chunk.buffer);
  dv.setUint32(0, 9);
  chunk.set([0x70, 0x48, 0x59, 0x73], 4);           // 'pHYs'
  dv.setUint32(8, ppm); dv.setUint32(12, ppm); chunk[16] = 1;
  dv.setUint32(17, crc32(chunk.subarray(4, 17)));
  const ihdrEnd = 8 + 8 + 13 + 4;                   // signature + IHDR(len,type,data,crc)
  const out = new Uint8Array(png.length + chunk.length);
  out.set(png.subarray(0, ihdrEnd), 0);
  out.set(chunk, ihdrEnd);
  out.set(png.subarray(ihdrEnd), ihdrEnd + chunk.length);
  return out;
}

// ---- the footer every export carries: the site's logo, the work item, when and who --------
// src: a logo URL / data URI (an SVG too) → { canvas, png bytes, ratio } at a print-worthy size.
export async function loadLogo(src) {
  if (!src) return null;
  const img = await new Promise((res) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });
  if (!img) return null;
  const w = img.naturalWidth || img.width || 300; const h = img.naturalHeight || img.height || 100;
  const H = 180; const W = Math.max(1, Math.round((w * H) / h));
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  c.getContext('2d').drawImage(img, 0, 0, W, H);
  const blob = await new Promise((r) => c.toBlob(r, 'image/png'));
  return { canvas: c, png: blob ? new Uint8Array(await blob.arrayBuffer()) : null, ratio: W / H };
}
export const FOOTER_H = 46;   // board px (PNG); the PDF's is in points

// images: Map fileId → drawable. footer: { logo (loadLogo), left, right }
// frame: crop to this box (a marked-up drawing) instead of the elements' own → Blob (image/png)
export async function exportPng(elements, images, { scale = 2, background = '#ffffff', padding = 32, footer = null, frame = null } = {}) {
  const list = [...elements].sort(byZ);
  const box = frame ? { x: frame.x, y: frame.y, w: frame.w, h: frame.h } : exportBox(list, padding);
  if (!box) throw new Error('There is nothing on the whiteboard to export.');
  const fh = footer ? FOOTER_H : 0;
  const s = Math.min(scale, maxScale({ ...box, h: box.h + fh }));
  const { w, h } = pngSize({ ...box, h: box.h + fh }, s);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser could not draw a picture this large — choose a lower resolution.');
  if (background && background !== 'transparent') { ctx.fillStyle = background; ctx.fillRect(0, 0, w, h); }
  ctx.setTransform(s, 0, 0, s, -box.x * s, -box.y * s);
  drawCanvas(ctx, list, images, s);
  if (footer) {
    // a white band under the drawing: logo left, the work item beside it, when / who on the right
    ctx.setTransform(s, 0, 0, s, 0, box.h * s);
    const W = w / s;
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, fh);
    ctx.fillStyle = '#e2e8f0'; ctx.fillRect(16, 0, W - 32, 1);
    let x = 16;
    if (footer.logo?.canvas) {
      const lh = 24; const lw = lh * footer.logo.ratio;
      ctx.drawImage(footer.logo.canvas, x, (fh - lh) / 2, lw, lh);
      x += lw + 14;
    }
    ctx.fillStyle = '#334155'; ctx.font = '600 12px Helvetica, Arial, sans-serif'; ctx.textBaseline = 'middle';
    ctx.fillText(footer.left || '', x, fh / 2);
    ctx.fillStyle = '#64748b'; ctx.font = '400 11px Helvetica, Arial, sans-serif'; ctx.textAlign = 'right';
    ctx.fillText(footer.right || '', W - 16, fh / 2);
    ctx.textAlign = 'left';
  }
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
  if (!blob) throw new Error('This browser could not make a picture this large — choose a lower resolution.');
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return new Blob([withDpi(bytes, 96 * s)], { type: 'image/png' });
}

// ---- PDF -----------------------------------------------------------------------------
// The pictures as jsPDF wants them: JPEG and PNG bytes as they are, anything
// else (GIF, WebP) and every TURNED picture drawn to a PNG first.
// files: Map fileId → { blob, mime, img (decoded drawable), width, height }
export async function pdfImages(elements, files) {
  const out = new Map();
  const toPngBytes = async (canvas) => new Uint8Array(await (await new Promise((r) => canvas.toBlob(r, 'image/png'))).arrayBuffer());
  for (const el of elements) {
    if (el.type !== 'image') continue;
    const f = files.get(el.fileId);
    if (!f || !f.img) continue;
    if (!out.has(el.fileId)) {
      if ((f.mime === 'image/jpeg' || f.mime === 'image/png') && f.blob) {
        out.set(el.fileId, { data: new Uint8Array(await f.blob.arrayBuffer()), format: f.mime === 'image/jpeg' ? 'JPEG' : 'PNG' });
      } else {
        const c = document.createElement('canvas');
        c.width = f.width || f.img.naturalWidth || f.img.width; c.height = f.height || f.img.naturalHeight || f.img.height;
        c.getContext('2d').drawImage(f.img, 0, 0, c.width, c.height);
        out.set(el.fileId, { data: await toPngBytes(c), format: 'PNG' });
      }
    }
    if (el.angle) {
      // turn it on a canvas at the picture's own resolution (capped)
      const natW = f.width || f.img.naturalWidth || f.img.width; const natH = f.height || f.img.naturalHeight || f.img.height;
      const k = Math.min(4096 / Math.max(natW, natH), Math.max(natW / el.w, natH / el.h, 1));
      const [cx, cy] = center(el);
      const box = boxOfPoints(corners({ x: el.x, y: el.y, w: el.w, h: el.h }).map(([x, y]) => rotate(x, y, cx, cy, el.angle)));
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.ceil(box.w * k)); c.height = Math.max(1, Math.ceil(box.h * k));
      const ctx = c.getContext('2d');
      ctx.setTransform(k, 0, 0, k, -box.x * k, -box.y * k);
      ctx.translate(cx, cy); ctx.rotate(el.angle); ctx.translate(-cx, -cy);
      ctx.drawImage(f.img, el.x, el.y, el.w, el.h);
      out.set(`el:${el.id}`, { data: await toPngBytes(c), x: box.x, y: box.y, w: box.w, h: box.h });
    }
  }
  return out;
}

// page: 'board' | 'A4' | 'A3' | 'A2' | 'A1' | 'A0' | 'Letter' | 'sheet' (a drawing's own paper
// size, else its own size); orientation: 'auto' | 'portrait' | 'landscape'
// footer: { logo (loadLogo), left, right } | a plain string (left only)
// frame: crop to this box (a marked-up drawing) → { blob, page: { w, h }, scale }
export async function exportPdf(elements, images, opts = {}) {
  return exportPdfSheets([{ elements, frame: opts.frame || null }], images, opts);
}

// One page per sheet: [{ elements, frame?, footer? }] (a sheet's own footer wins).
export async function exportPdfSheets(sheets, images, { page = 'A3', orientation = 'auto', footer = null, title = '', padding = 16, jsPDF: injected } = {}) {
  if (!sheets.length) throw new Error('There is nothing on the whiteboard to export.');
  const JsPDF = injected || (await import('jspdf')).jsPDF;
  let doc = null; let first = null;
  for (const sheet of sheets) {
    const list = [...sheet.elements].sort(byZ);
    const box = sheet.frame ? { x: sheet.frame.x, y: sheet.frame.y, w: sheet.frame.w, h: sheet.frame.h } : exportBox(list, padding);
    if (!box || !list.length) throw new Error('There is nothing on the whiteboard to export.');
    const footSrc = sheet.footer !== undefined ? sheet.footer : footer;
    const foot = typeof footSrc === 'string' ? (footSrc ? { left: footSrc } : null) : footSrc;
    const footerH = foot ? 26 : 0;
    const paper = page === 'sheet' ? paperOf(box.w, box.h) || 'board' : page;
    const margin = paper === 'board' ? 18 : 28;
    let pw; let ph; let s;
    if (paper === 'board') {
      s = 0.75;                                       // 1 board unit = 1 screen px = ¾ pt
      const limit = 14000;                            // the PDF page-size limit (~200 in), with room
      s = Math.min(s, (limit - 2 * margin) / box.w, (limit - 2 * margin - footerH) / box.h);
      pw = box.w * s + 2 * margin; ph = box.h * s + 2 * margin + footerH;
    } else {
      const [a, b] = PAGE_SIZES[paper] || PAGE_SIZES.A3;
      const landscape = page === 'sheet' ? box.w > box.h : orientation === 'landscape' || (orientation === 'auto' && box.w > box.h);
      pw = landscape ? Math.max(a, b) : Math.min(a, b);
      ph = landscape ? Math.min(a, b) : Math.max(a, b);
      s = Math.min((pw - 2 * margin) / box.w, (ph - 2 * margin - footerH) / box.h);
    }
    if (!doc) {
      doc = new JsPDF({ unit: 'pt', format: [pw, ph], orientation: pw > ph ? 'landscape' : 'portrait', compress: true });
      if (title) doc.setProperties({ title, creator: 'Zebra Stride' });
      first = { w: pw, h: ph, s };
    } else {
      doc.addPage([pw, ph], pw > ph ? 'landscape' : 'portrait');
    }
    const contentW = box.w * s; const contentH = box.h * s;
    const mx = (pw - contentW) / 2; const my = margin + (ph - 2 * margin - footerH - contentH) / 2;
    // a drawing is cropped to its edges: ink that runs past them is left out
    const clip = !!sheet.frame;
    if (clip) { doc.saveGraphicsState(); doc.rect(mx, my, contentW, contentH, null); doc.clip(); doc.discardPath(); }
    drawPdf(doc, list, images, { s, ox: box.x, oy: box.y, mx, my });
    if (clip) doc.restoreGraphicsState();
    if (foot) {
      // the footer band: a hairline, the site's logo, the work item, when / who
      const fy = ph - margin / 2 - footerH / 2 - 2;     // the band's middle
      doc.setDrawColor('#e2e8f0'); doc.setLineWidth(0.6); doc.setLineDashPattern([], 0);
      doc.line(margin, fy - footerH / 2, pw - margin, fy - footerH / 2);
      let x = margin;
      if (foot.logo?.png) {
        const lh = 15; const lw = lh * foot.logo.ratio;
        doc.addImage(foot.logo.png, 'PNG', x, fy - lh / 2 + 2, lw, lh, 'wb-site-logo', 'FAST');
        x += lw + 10;
      }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor('#334155');
      doc.text(foot.left || '', x, fy + 5, { baseline: 'alphabetic', maxWidth: Math.max(60, pw - margin - x - 180) });
      if (foot.right) {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor('#64748b');
        doc.text(foot.right, pw - margin, fy + 5, { baseline: 'alphabetic', align: 'right' });
      }
    }
  }
  return { blob: doc.output('blob'), page: { w: first.w, h: first.h }, scale: first.s, pages: sheets.length };
}

export function safeFileName(s) {
  return String(s || 'Whiteboard').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120) || 'Whiteboard';
}

export function download(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
